<template>
  <div>
    <Spinner v-if="isSearching" />

    <div class="notification is-info" v-if="hasEmptySearchResult">No matching user found.</div>

    <div class="notification is-danger" v-if="searchError">{{ searchError }}</div>

    <UsersList
      v-if="showUsersList"
      :users="searchResult"
      :selectedUserId="selectedUserId"
      @user-selected="selectUser($event)"
    />
  </div>
</template>

<script>
import { eventManager, USER_STATE_CHANGED_EVENT } from '@/eventManager.js'
import Spinner from '@/components/utils/Spinner'
import UsersList from './UsersList'

import {
  USERS_SEARCH_STORE_NAMESPACE,
  SEARCH_USERS,
  IS_SEARCHING,
  GET_SEARCH_RESULT,
  GET_SEARCH_ERROR,
  HAS_EMPTY_SEARCH_RESULT,
  INVALIDATE_SEARCH_RESULTS
} from './usersSearchStore'

import { GET_USER_ID, SET_USER, INVALIDATE_GPS_DEVICES } from './userStore'

import { SET_HUNT_AREA } from '@/modules/huntareas/huntAreaStore'

import { SET_HUNT } from '@/modules/hunts/huntStore'

import { RESET_USERS_CACHE } from '@/modules/users/usersCacheStore.js'

import { RESET_HUNT_AREAS_CACHE } from '@/modules/huntareas/huntAreasCacheStore'

import { SET_WEHUNT_GPS } from '@/modules/gps/weHuntGPSStore'

import { SET_GPS } from '@/modules/gps/gpsDeviceStore'

export default {
  name: 'UserSearch',
  components: { Spinner, UsersList },

  props: {
    query: {
      type: String,
      default: ''
    },

    selectedUserStorePath: {
      type: String,
      required: true
    },

    selectedHuntAreaStorePath: {
      type: String,
      required: true
    },

    selectedHuntStorePath: {
      type: String,
      required: true
    },

    usersCacheStorePath: {
      type: String,
      required: true
    },

    huntAreasCacheStorePath: {
      type: String,
      required: true
    },

    weHuntGPSStorePath: {
      type: String,
      required: true
    },

    gpsDeviceStorePath: {
      type: String,
      required: true
    }
  },

  data() {
    return {}
  },

  mounted() {
    if (this.$route.name === 'usersSearch') {
      // Only reset searc when a new search has started
      this.resetSelectedUser()

      const invalidateSearchResultAction = `${USERS_SEARCH_STORE_NAMESPACE}/${INVALIDATE_SEARCH_RESULTS}`
      this.$store.dispatch(invalidateSearchResultAction)
    }

    this.performSearch()

    // Make sure that search results are reset whenever a user state changes to prevent stale data.
    eventManager.$on(USER_STATE_CHANGED_EVENT, this.handleUserStateChanged)
  },

  beforeDestroy() {
    eventManager.$off(USER_STATE_CHANGED_EVENT, this.handleUserStateChanged)
  },

  methods: {
    handleUserStateChanged() {
      // Reset selected user GPS devices
      const invalidateGPSDevicesAction = `${this.selectedUserStorePath}/${INVALIDATE_GPS_DEVICES}`
      this.$store.dispatch(invalidateGPSDevicesAction)

      const invalidateSearchResultAction = `${USERS_SEARCH_STORE_NAMESPACE}/${INVALIDATE_SEARCH_RESULTS}`
      this.$store.dispatch(invalidateSearchResultAction)

      this.performSearch()
    },

    performSearch() {
      const query = this.query
      const searchUsersAction = `${USERS_SEARCH_STORE_NAMESPACE}/${SEARCH_USERS}`
      this.$store.dispatch(searchUsersAction, { query })
    },

    selectUser(user) {
      const setUserAction = `${this.selectedUserStorePath}/${SET_USER}`
      this.$store.dispatch(setUserAction, { user })

      const storePath = this.selectedUserStorePath
      const userId = user.id
      this.$router.push({
        name: 'userDetails',
        params: { storePath, userId },
        query: this.$route.query
      })
    },

    resetSelectedUser() {
      // Reset selected user
      const setUserAction = `${this.selectedUserStorePath}/${SET_USER}`
      this.$store.dispatch(setUserAction, { user: null })

      // Reset selected hunt area
      const setHuntAreaAction = `${this.selectedHuntAreaStorePath}/${SET_HUNT_AREA}`
      this.$store.dispatch(setHuntAreaAction, { user: null })

      // Reset selected hunt
      const setHuntAction = `${this.selectedHuntStorePath}/${SET_HUNT}`
      this.$store.dispatch(setHuntAction, { user: null })

      // Reset users cache
      const resetUsersCacheAction = `${this.usersCacheStorePath}/${RESET_USERS_CACHE}`
      this.$store.dispatch(resetUsersCacheAction)

      // Reset hunt areas cache
      const resetHuntAreasCacheAction = `${this.huntAreasCacheStorePath}/${RESET_HUNT_AREAS_CACHE}`
      this.$store.dispatch(resetHuntAreasCacheAction)

      // Reset selected wehunt gps
      const setWeHuntGPSAction = `${this.weHuntGPSStorePath}/${SET_WEHUNT_GPS}`
      this.$store.dispatch(setWeHuntGPSAction, { gps: null })

      // Reset selected gps device
      const setGPSAction = `${this.gpsDeviceStorePath}/${SET_GPS}`
      this.$store.dispatch(setGPSAction, { gps: null })
    }
  },

  computed: {
    showUsersList() {
      if (!this.searchResult) {
        return false
      }

      return this.searchResult.length > 0
    },

    isSearching() {
      const isSearching = `${USERS_SEARCH_STORE_NAMESPACE}/${IS_SEARCHING}`
      return this.$store.getters[isSearching]
    },

    searchResult() {
      const getSearchResult = `${USERS_SEARCH_STORE_NAMESPACE}/${GET_SEARCH_RESULT}`
      return this.$store.getters[getSearchResult]
    },

    searchError() {
      const getSearchError = `${USERS_SEARCH_STORE_NAMESPACE}/${GET_SEARCH_ERROR}`
      return this.$store.getters[getSearchError]
    },

    selectedUserId() {
      const getUserId = `${this.selectedUserStorePath}/${GET_USER_ID}`
      return this.$store.getters[getUserId]
    },

    hasEmptySearchResult() {
      const hasEmptySearchResult = `${USERS_SEARCH_STORE_NAMESPACE}/${HAS_EMPTY_SEARCH_RESULT}`
      return this.$store.getters[hasEmptySearchResult]
    }
  },

  watch: {
    searchResult() {
      if (!this.searchResult) {
        return
      }

      // Automatically select a user after a new search if result count is 1
      if (this.searchResult.length === 1 && this.$route.name === 'usersSearch') {
        this.selectUser(this.searchResult[0])
      }
    }
  }
}
</script>

<style lang="scss" scoped></style>
